import { Grid, useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import CommonFormField from "../../common/components/CommonFormField";
import CommonSelect from "../../common/components/CommonSelect";
import CommonCard from "../../common/components/CommonCard";
import { Academics, FirstTimes, ProficiencyLevels, Profiles, ProfilesObj, ProjectDurations, ProjectTypeObj, ProjectTypes, removeEmptyFields, Status, Types } from "../../common/const";
import CommonButton from "../../common/components/CommonButton";
import { Add } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import { createProjectResearch } from "../../api/resourceApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { getProjectList } from "../../api/projectBankApi";
import { setProjectList } from "../../features/projectSlice";

const ProjectInfo = ({
    formData,
    setFormData
}) => {
    const theme = useTheme();
    const { user } = useSelector((state) => state.auth);
    const { projectList } = useSelector((state) => state.project);
    const dispatch = useDispatch();
    const [errors, setErrors] = useState({});

    const getProjects = async () => {
        const response = await getProjectList();
        if (response?.data?.data?.length > 0) {
            dispatch(setProjectList({
                list: response.data.data.map((a) => ({
                    label: a.project_name,
                    value: a.project_bank_id
                }))
            }))
        }
        dispatch(setLoading({
            loading: false
        }));
    }

    useEffect(() => {
        dispatch(setLoading({
            loading: true
        }));
        getProjects();
    }, [formData.projectInfo.projectype]);

    const validateField = (name, value, projectInfo) => {
        switch (name) {
            case 'name':
                if (!value && projectInfo.projecttype === ProjectTypeObj.ext) return `${projectInfo.profile === ProfilesObj.Research ? 'Research' : 'Project'} Name is required`;
                return '';
            case 'bankid':
                if (!value && projectInfo.projecttype === ProjectTypeObj.dcp) return `Project Name is required`;
                return '';
            case 'type':
                if (!value) return `${projectInfo.profile === ProfilesObj.Research ? 'Research' : 'Project'} Type is required`;
                return '';
            case 'status':
                if (!value) return 'Status is required';
                return '';
            default:
                return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            projectInfo: {
                ...prev.projectInfo,
                [name]: value,
            },
        }));
        // Clear error for the field being changed
        if (errors[name]) {
            const error = validateField(name, value, formData.projectInfo.profile);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData.projectInfo).forEach(key => {
            const error = validateField(key, formData.projectInfo[key], formData.projectInfo);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            dispatch(setLoading({
                loading: true
            }));
            const response = await createProjectResearch(removeEmptyFields({
                "project_profile": formData.projectInfo.profile,
                "project_name": formData.projectInfo.name !== '' ? formData.projectInfo.name : projectList.find((a) => a.value === formData.projectInfo.bankid)?.label,
                "project_level": formData.projectInfo.projectlevel !== '' ? formData.projectInfo.projectlevel : null,
                "project_source": formData.projectInfo.projectype !== '' ? formData.projectInfo.projectype : null,
                "project_type": formData.projectInfo.type,
                "project_bank_id": formData.projectInfo.bankid !== '' ? formData.projectInfo.bankid : null,
                "it_first_time": formData.projectInfo.firstTime !== '' ? formData.projectInfo.firstTime : null,
                "project_duration": formData.projectInfo.duration,
                "status": formData.projectInfo.status,
                "total_hours": formData.projectInfo.totalhour,
                "is_active": true,
                "start_date": formData.projectInfo.startdate !== '' ? new Date(formData.projectInfo.startdate).toISOString() : null,
                "end_date": formData.projectInfo.enddate !== '' ? new Date(formData.projectInfo.enddate).toISOString() : null,
                "student_grade": localStorage.getItem('current_grade'),
                "student_id": user.student_id
            }));
            if (response.data.project_id) {
                showNotification(`${formData.projectInfo.profile === ProfilesObj.Research ? 'Research' : 'Project'} added successfully`, 'success');
                dispatch(setLoading({
                    loading: false
                }));
                setFormData((prev) => ({
                    ...prev,
                    projectInfo: {
                        profile: Profiles[0].value,
                        projectype: ProjectTypes[0].value,
                        status: '',
                        firstTime: FirstTimes[0].value,
                        name: '',
                        bankid: '',
                        type: '',
                        projectlevel: '',
                        duration: '',
                        totalhour: '',
                        startdate: '',
                        enddate: '',
                    },
                }));
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    const getMinDate = () => {
        const date = formData.projectInfo.startdate ? new Date(formData.projectInfo.startdate) : new Date();
        date.setDate(date.getDate() + 1); // Add 7 days
        return date.toISOString().split('T')[0];
    };

    return (
        <Grid container spacing={1.5} mt={0.5} mb={2}>
            <Grid item xs={12}>
                <CommonCard
                    marginTop={0.5}
                    borderRadius={1}
                    border={`1px solid ${theme.palette.background.light}`}
                    content={
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} md={formData.projectInfo.profile === ProfilesObj.Project ? 6 : 12}>
                                <CommonSelect
                                    label="Select Profile"
                                    name="profile"
                                    value={formData.projectInfo.profile}
                                    onChange={handleInputChange}
                                    placeholder="Select Profile"
                                    options={Profiles}
                                />
                            </Grid>
                            {formData.projectInfo.profile === ProfilesObj.Project && (
                                <Grid item xs={12} md={6}>
                                    <CommonSelect
                                        label="Project Type (DCP/External)"
                                        name="projectype"
                                        value={formData.projectInfo.projectype}
                                        onChange={handleInputChange}
                                        placeholder="Select Project Type"
                                        options={ProjectTypes}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} md={6}>
                                <CommonSelect
                                    label="Status"
                                    name="status"
                                    value={formData.projectInfo.status}
                                    onChange={handleInputChange}
                                    placeholder="Select Status"
                                    error={errors.status}
                                    options={formData.projectInfo.profile === ProfilesObj.Project ? Status : Academics}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonSelect
                                    label="Is it First Time/Expanding Existing"
                                    name="firstTime"
                                    value={formData.projectInfo.firstTime}
                                    onChange={handleInputChange}
                                    placeholder="Select Is it First Time/Expanding Existing"
                                    options={FirstTimes}
                                />
                            </Grid>
                            {formData.projectInfo.projectype === ProjectTypeObj.ext || formData.projectInfo.profile === ProfilesObj.Research ? (
                                <Grid item xs={12} md={6}>
                                    <CommonFormField
                                        label={`${formData.projectInfo.profile === ProfilesObj.Research ? 'Research' : 'Project'} Name`}
                                        type="email"
                                        name="name"
                                        value={formData.projectInfo.name}
                                        onChange={handleInputChange}
                                        error={errors.name}
                                        placeholder={`Enter ${formData.projectInfo.profile === ProfilesObj.Research ? 'Research' : 'Project'} Name`}
                                    />
                                </Grid>
                            ) : (
                                <Grid item xs={12} md={6}>
                                    <CommonSelect
                                        label="Project Name"
                                        name="bankid"
                                        value={formData.projectInfo.bankid}
                                        onChange={handleInputChange}
                                        placeholder="Select Project Name"
                                        error={errors.bankid}
                                        options={projectList}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} md={6}>
                                <CommonSelect
                                    label={`${formData.projectInfo.profile === ProfilesObj.Research ? 'Research' : 'Project'} Type`}
                                    name="type"
                                    value={formData.projectInfo.type}
                                    onChange={handleInputChange}
                                    placeholder={`Select ${formData.projectInfo.profile === ProfilesObj.Research ? 'research' : 'project'} Type`}
                                    error={errors.type}
                                    options={Types}
                                />
                            </Grid>
                            {formData.projectInfo.profile === ProfilesObj.Project && (
                                <Grid item xs={12} md={6}>
                                    <CommonSelect
                                        label='Project Level'
                                        name="projectlevel"
                                        value={formData.projectInfo.projectlevel}
                                        onChange={handleInputChange}
                                        placeholder='Select Project Level'
                                        options={ProficiencyLevels}
                                    />
                                </Grid>
                            )}
                            {formData.projectInfo.profile === ProfilesObj.Project ? (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <CommonFormField
                                            label="Duration (Hours)"
                                            type="email"
                                            name="duration"
                                            value={formData.projectInfo.duration}
                                            onChange={handleInputChange}
                                            placeholder='Enter Duration'
                                        />
                                    </Grid>
                                    {formData.projectInfo.projectype === ProjectTypeObj.ext && (
                                        <Grid item xs={12} md={12}>
                                            <CommonFormField
                                                label='Total Hours (Hours)'
                                                type="email"
                                                name="totalhour"
                                                value={formData.projectInfo.totalhour}
                                                onChange={handleInputChange}
                                                placeholder='Enter Total Hours'
                                            />
                                        </Grid>
                                    )}
                                </>
                            ) : (
                                <Grid item xs={12} md={12}>
                                    <CommonSelect
                                        label="Select Duration"
                                        name="duration"
                                        value={formData.projectInfo.duration}
                                        onChange={handleInputChange}
                                        placeholder="Select month"
                                        options={ProjectDurations}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} md={['COMPLETED', 'PAST']?.includes(formData.projectInfo.status) ? 6 : 12}>
                                <CommonFormField
                                    label="Start Date"
                                    type="date"
                                    name="startdate"
                                    value={formData.projectInfo.startdate}
                                    onChange={handleInputChange}
                                    error={errors.startdate}
                                    placeholder="Select start date"
                                />
                            </Grid>
                            {['COMPLETED', 'PAST']?.includes(formData.projectInfo.status) && (
                                <Grid item xs={12} md={6}>
                                    <CommonFormField
                                        label="End Date"
                                        type="date"
                                        name="enddate"
                                        value={formData.projectInfo.enddate}
                                        onChange={handleInputChange}
                                        placeholder="Select end date"
                                        minDate={getMinDate()}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <CommonButton
                                    label={`Add ${formData.projectInfo.profile === ProfilesObj.Research ? 'Research' : 'Project'}`}
                                    background={theme.palette.secondary.main}
                                    color={theme.palette.background.main}
                                    fontWeight={600}
                                    handleClick={() => handleSubmit()}
                                    startIcon={<Add />}
                                    marginBottom={1}
                                />
                            </Grid>
                        </Grid>
                    }
                    paddingBottom={0}
                    padding="16px 16px 0px 16px"
                />
            </Grid>
        </Grid>
    )
}

export default ProjectInfo;